import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { Content } from 'antd/es/layout/layout';
import AppHeader from '../../../components/Dashboard/AppHeader';
import studentbar from '../../../assets/icons/studentbar.svg';
import userStore from '../../../zustand/store/userStore';
import TodoList from '../../../components/TodoList/TodoList';
import { Avatar, Table, message as antdMessage } from 'antd';
import Calender from '../../../components/Calender/Calender';

const hoursData = [
  { name: 'Jan', study: 35, exams: 20 },
  { name: 'Feb', study: 25, exams: 15 },
  { name: 'Mar', study: 45, exams: 30 },
  { name: 'Apr', study: 30, exams: 25 },
  { name: 'May', study: 20, exams: 10 },
];

const todoList = [
  { task: "Developing Restaurant Apps", category: "Programming", time: "08:00 AM", completed: false },
  { task: "Integrate API", category: "", time: "", completed: false },
  { task: "Slicing Home Screen", category: "", time: "", completed: false },
  { task: "Research Objective User", category: "Product Design", time: "02:40 PM", completed: false },
  { task: "Report Analysis P2P Business", category: "Business", time: "04:50 PM", completed: true }
];

const initialTodos = [
  { 
    id: '1',
    task: "Developing Restaurant Apps", 
    category: "Programming", 
    time: "08:00", 
    completed: false 
  },
  { 
    id: '2',
    task: "Integrate API", 
    category: "", 
    time: "", 
    completed: false 
  },
  { 
    id: '3',
    task: "Slicing Home Screen", 
    category: "", 
    time: "", 
    completed: false 
  },
  { 
    id: '4',
    task: "Research Objective User", 
    category: "Product Design", 
    time: "14:40", 
    completed: false 
  },
  { 
    id: '5',
    task: "Report Analysis P2P Business", 
    category: "Business", 
    time: "16:50", 
    completed: true 
  }
];

const TeacherDashboard = () => {
  const {user} = userStore();
  const currentDate = new Date(2024, 11);
  const days = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const columns = [
    { title: 'Rank', dataIndex: 'rank', key: 'rank' },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className="flex items-center">
          <Avatar
            className="mr-2"
            src={`https://i.pravatar.cc/150?u=${text}`}
          />
          {text}
        </div>
      ),
    },
    { title: 'Course', dataIndex: 'course', key: 'course' },
    { title: 'Hour', dataIndex: 'hour', key: 'hour' },
    {
      title: 'Point',
      dataIndex: 'point',
      key: 'point',
      render: (text) => <span className="text-teal-400">{text}</span>
    }
  ];

  return (
    <Content>
      <AppHeader
        title={`Welcome, ${user?.fullName} 👋`}
        content={'Let’s learn something new today!'}
      />
      <div className="relative bg-[#9747FF] text-white p-4 rounded-xl flex justify-between mt-4">
        <div className="font-[600] flex items-center lg:text-[24px]/[2] md:text-[18px]/[2] sm:text-[12px]/8">
          The central hub for navigation <br /> and accessing key features
        </div>
        <img src={studentbar} alt="" className="mr-4" />
      </div>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto space-y-6">

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Hours Spent Section */}
            <div className="lg:col-span-2 md:col-span-2 bg-white rounded-xl p-6 border-2 border-solid border-black">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Hours Spent</h2>
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-400 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Study</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-200 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Exams</span>
                  </div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="study" fill="#FB923C" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="exams" fill="#FED7AA" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Performance Section */}
            <div className="bg-white rounded-xl p-6 border-2 border-solid border-black">
              <div className="flex justify-between flex-col items-center mb-3 space-x-3">
                <h4 className="text-lg font-semibold">Performance</h4>
                <select className="text-sm text-gray-600 border rounded-md px-1 py-1">
                  <option>Monthly</option>
                </select>
              </div>
              <div className="flex justify-center items-center h-64">
                <div className="relative">
                  <svg className="w-48 h-48">
                    <circle
                      className="text-gray-200"
                      strokeWidth="12"
                      stroke="currentColor"
                      fill="transparent"
                      r="90"
                      cx="96"
                      cy="96"
                    />
                    <circle
                      className="text-teal-400"
                      strokeWidth="12"
                      strokeDasharray={565.48}
                      strokeDashoffset={565.48 * (1 - 0.75)}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="90"
                      cx="96"
                      cy="96"
                    />
                  </svg>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="text-sm text-gray-500">Your Point:</div>
                    <div className="text-2xl font-bold">8.966</div>
                  </div>
                </div>
              </div>
            </div>

            {/* calender section */}
            <Calender/>
          </div>

          {/* Todo Section - Now takes full width */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <TodoList initialTodos={initialTodos} />
            </div>
          </div>
        </div>
      </div>
    </Content>
  );
}

export default TeacherDashboard;